/**
 * The footer bar every dialog in this app ends with: an optional note on the
 * left, Cancel and a confirm button on the right (BON-472).
 *
 * Shared because the busy state is the part that gets forgotten, on BOTH
 * buttons. A confirm that stays live lets a CSR fire a second rotation, and the
 * second one wins the database while the first one's email is already on its
 * way. A Cancel that stays live is worse in a quieter way: it cannot call the
 * request back, so all it does is hide the answer from someone who now believes
 * nothing was sent.
 */
export function DialogFooterActions({
  note,
  cancelLabel = 'Cancel',
  onCancel,
  cancelRef,
  confirmLabel,
  confirmBusyLabel,
  confirmType = 'submit',
  onConfirm,
  busy = false,
}: {
  /** Left-hand context — what is locked, or whose name lands on the audit row. */
  note?: string;
  cancelLabel?: string;
  onCancel: () => void;
  /** Pass when the dialog wants focus to open on Cancel rather than on a field. */
  cancelRef?: React.RefObject<HTMLButtonElement>;
  confirmLabel: string;
  /** Shown while `busy`. Defaults to `confirmLabel`. */
  confirmBusyLabel?: string;
  confirmType?: 'submit' | 'button';
  onConfirm?: () => void;
  /** A request is in flight. Disables both buttons — see the file docblock. */
  busy?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-stone-200 px-6 py-4 dark:border-stone-700">
      <p className="text-xs text-stone-500 dark:text-stone-400">{note}</p>
      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          type="button"
          ref={cancelRef}
          onClick={onCancel}
          disabled={busy}
          // The dark hover had no colour of its own, so it inherited the light
          // one: near-white text on a near-white background, and the button
          // vanished under the pointer.
          className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-stone-600 dark:text-stone-200 dark:hover:bg-stone-700"
        >
          {cancelLabel}
        </button>
        <button
          type={confirmType}
          onClick={onConfirm}
          disabled={busy}
          className="rounded-lg bg-moss-600 px-4 py-2 text-sm font-semibold text-white hover:bg-moss-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? (confirmBusyLabel ?? confirmLabel) : confirmLabel}
        </button>
      </div>
    </div>
  );
}
